import Fuse from "fuse.js";
import { resolveCosing } from "./cosingResolver";

const DEFAULT_THRESHOLD = 0.2;

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const SECTION_HEADER_PATTERN = /^(fragrance oil|diluent|dilution|base|carrier|solvent|concentrate|accord|perfume oil|perfume concentrate)\s*$/i;
const TOTAL_VOLUME_HEADER_PATTERN = /^.+\(\s*\d*\.?\d+\s*(ml|mL|milliliters?|millilitres?|l|L|liters?|litres?)\s+total\s*\)\s*$/i;
const VOLUME_UNIT_PATTERN = "(ml|mL|milliliters?|millilitres?|l|L|liters?|litres?)";

const normalizeCas = (value) => {
  const normalizedValue = String(value || "").replace(/\s+/g, "").trim();
  return normalizedValue.toLowerCase() === "n/a" ? "" : normalizedValue;
};
const normalizeCasList = (value) =>
  (Array.isArray(value) ? value : [value])
    .flatMap((entry) => String(entry || "").split(/\s*,\s*/))
    .map(normalizeCas)
    .filter(Boolean);

const normalizeUnit = (unit) => {
  const normalized = normalizeText(unit).replace(/\s+/g, "");
  if (!normalized) return "";
  if (normalized === "%" || normalized === "percent" || normalized === "percentage") return "%";
  if (normalized === "ppm") return "ppm";
  if (normalized === "mg/g" || normalized === "mgperg") return "mg/g";
  if (normalized === "mg/ml" || normalized === "mgperml") return "mg/mL";
  return normalized;
};

const normalizeVolumeUnit = (unit) => {
  const normalized = normalizeText(unit).replace(/\./g, "");
  if (!normalized) return "";
  if (["ml", "milliliter", "milliliters", "millilitre", "millilitres"].includes(normalized)) return "mL";
  if (["l", "liter", "liters", "litre", "litres"].includes(normalized)) return "L";
  return "";
};

const volumeToMilliliters = (value, unit) => {
  const numericValue = Number(value);
  const normalizedUnit = normalizeVolumeUnit(unit);
  if (!Number.isFinite(numericValue) || !normalizedUnit) return null;
  return normalizedUnit === "L" ? numericValue * 1000 : numericValue;
};

const isSectionHeaderLine = (line) =>
  SECTION_HEADER_PATTERN.test(String(line || "").trim()) ||
  TOTAL_VOLUME_HEADER_PATTERN.test(String(line || "").trim());

const extractDilutionFactor = (ingredientName) => {
  const match = String(ingredientName || "").match(/\((\d*\.?\d+)\s*(%|percent|percentage)\)\s*$/i);
  if (!match) return { factor: 1, label: "" };

  const dilution = parseConcentration(match[1], match[2]);
  if (!dilution.verified || dilution.percentage === null) {
    return { factor: 1, label: "" };
  }

  return {
    factor: dilution.percentage / 100,
    label: `${match[1]} %`,
  };
};

const cleanIngredientName = (ingredientName) =>
  String(ingredientName || "")
    .replace(/\((\d*\.?\d+)\s*(%|percent|percentage)\)\s*$/i, "")
    .replace(/^optional\s+/i, "")
    .replace(/\s+/g, " ")
    .trim();

const parseConcentration = (value, unit) => {
  const numericValue = Number(value);
  const normalizedUnit = normalizeUnit(unit);

  if (!Number.isFinite(numericValue)) {
    return {
      percentage: null,
      unit: normalizedUnit || "",
      verified: false,
      issue: "Concentration value is not numeric.",
    };
  }

  if (normalizedUnit === "%") {
    return { percentage: numericValue, unit: "%", verified: true, issue: "" };
  }

  if (normalizedUnit === "ppm") {
    return { percentage: numericValue / 10000, unit: "ppm", verified: true, issue: "" };
  }

  if (normalizedUnit === "mg/g") {
    return { percentage: numericValue / 10, unit: "mg/g", verified: true, issue: "" };
  }

  if (normalizedUnit === "mg/mL") {
    return {
      percentage: null,
      unit: "mg/mL",
      verified: false,
      issue: "mg/mL requires density before conversion to percentage.",
    };
  }

  return {
    percentage: null,
    unit: normalizedUnit || "",
    verified: false,
    issue: "Concentration unit is missing or unsupported.",
  };
};

const toStoredPercentage = (value) => {
  if (value === null || value === undefined || value === "" || value === "N/A") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const buildIngredientIndex = (data) =>
  Object.entries(data || {}).map(([docName, value]) => ({
    docName,
    cas_numbers: normalizeCasList(value?.ingredient?.cas_number),
    cas_number: normalizeCasList(value?.ingredient?.cas_number)[0] || "",
    chemical_name: value?.ingredient?.name || "",
    synonyms: value?.ingredient?.synonyms || [],
    commercial_names: value?.ingredient?.commercial_names || [],
    raw: value,
  }));

// Common ingredients whose short names collide with fuzzy matches
// Maps normalized name → the CAS number to use for exact lookup
// Known non-IFRA cosmetic ingredients — valid but not in the fragrance dataset
// These pass through as valid with no threshold check
const NON_IFRA_PASSTHROUGH = new Set([
  "dipropylene glycol", "dpg", "isopropyl myristate", "ipm",
  "phenoxyethanol", "tocopherol", "bht", "butylated hydroxytoluene",
  "diethyl phthalate", "dep", "triethyl citrate", "benzyl benzoate",
  "propylene glycol", "glycerin", "water", "aqua",
  "cetyl alcohol", "stearyl alcohol", "cetearyl alcohol",
  "isododecane", "dimethicone", "cyclomethicone", "cyclopentasiloxane",
  "caprylic/capric triglyceride", "parfum", "fragrance",
  // Mineral / structural cosmetic ingredients commonly used in pressed
  // powders, lotions, and base formulations — explicit passthrough so that
  // short names like "Talc" don't get fuzzy-matched to fragrance ingredients.
  "talc", "talcum", "kaolin", "silica", "mica", "zinc oxide", "titanium dioxide",
  // PFAS ingredients commonly added to cosmetics (anti-stick, slip agents).
  "ptfe", "polytetrafluoroethylene", "pfoa", "pfos",
]);

const COMMON_ALIASES = {
  alcohol: "64-17-5",
  ethanol: "64-17-5",
  "ethyl alcohol": "64-17-5",
  "sd alcohol": "64-17-5",
  "denatured alcohol": "64-17-5",
  "alcohol denat": "64-17-5",
  "alcohol denat.": "64-17-5",
  "dipropylene glycol": "25265-71-8",
  "dpg": "25265-71-8",
  "isopropyl myristate": "110-27-0",
  "ipm": "110-27-0",
  "phenoxyethanol": "122-99-6",
  "tocopherol": "59-02-9",
  "bht": "128-37-0",
  "diethyl phthalate": "84-66-2",
  "dep": "84-66-2",
  "alpha-isomethyl ionone": "127-51-5",
  "α-isomethyl ionone": "127-51-5",
  // Minerals
  talc: "14807-96-6",
  talcum: "14807-96-6",
  kaolin: "1332-58-7",
  silica: "7631-86-9",
  mica: "12001-26-2",
  "zinc oxide": "1314-13-2",
  "titanium dioxide": "13463-67-7",
  // PFAS
  ptfe: "9002-84-0",
  polytetrafluoroethylene: "9002-84-0",
  pfoa: "335-67-1",
  pfos: "1763-23-1",
};

const findExactIngredientMatch = (index, ingredientName, casNumber) => {
  const normalizedCas = normalizeCas(casNumber);
  if (normalizedCas) {
    const casMatch = index.find((entry) => entry.cas_numbers.includes(normalizedCas));
    if (casMatch) {
      return { ...casMatch, matchType: "cas-exact" };
    }
  }

  const normalizedName = normalizeText(ingredientName);
  if (!normalizedName) {
    return null;
  }

  // Check common aliases first — prevents "Alcohol" → "Anisyl alcohol" fuzzy match
  const aliasCas = COMMON_ALIASES[normalizedName];
  if (aliasCas) {
    const aliasMatch = index.find((entry) => entry.cas_numbers.includes(aliasCas));
    if (aliasMatch) {
      return { ...aliasMatch, matchType: "alias-exact" };
    }
  }

  const nameMatch = index.find((entry) => {
    const candidates = [
      entry.chemical_name,
      ...entry.synonyms,
      ...entry.commercial_names,
    ].map(normalizeText);

    return candidates.includes(normalizedName);
  });

  return nameMatch ? { ...nameMatch, matchType: "name-exact" } : null;
};

const findFuzzyIngredientMatch = (index, searchTerm, threshold = DEFAULT_THRESHOLD) => {
  if (!normalizeText(searchTerm)) {
    return null;
  }

  const fuse = new Fuse(index, {
    includeScore: true,
    threshold,
    keys: ["chemical_name", "synonyms", "commercial_names", "cas_number"],
  });

  const [topResult] = fuse.search(searchTerm);
  return topResult ? { ...topResult.item, score: topResult.score, matchType: "fuzzy" } : null;
};

export const fuzzySearchIngredientByNameThenCAS = (data, searchTerm, threshold = DEFAULT_THRESHOLD) => {
  if (typeof searchTerm !== "string" || !searchTerm.trim()) {
    return null;
  }

  const index = buildIngredientIndex(data);
  return findExactIngredientMatch(index, searchTerm, "") || findFuzzyIngredientMatch(index, searchTerm, threshold);
};

export const fuzzySearchIngredient = (data, searchTerm, threshold = DEFAULT_THRESHOLD) => {
  if (typeof searchTerm !== "string" || !searchTerm.trim()) {
    return null;
  }

  const index = buildIngredientIndex(data);
  const normalizedCas = normalizeCas(searchTerm);
  return (
    index.find((entry) => entry.cas_numbers.includes(normalizedCas)) ||
    findFuzzyIngredientMatch(index, normalizedCas, threshold)
  );
};

export const parseFormulaText = (inputText) => {
  if (typeof inputText !== "string" || !inputText.trim()) {
    return [];
  }

  const lines = inputText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const volumeEntries = lines
    .map((line) => {
      if (isSectionHeaderLine(line)) {
        return null;
      }

      const match = line.match(
        new RegExp(`^(.*?)(?:\\s*[:\\-–—]\\s*|\\s+)(\\d*\\.?\\d+)\\s*${VOLUME_UNIT_PATTERN}\\s*$`, "i")
      );

      if (!match) {
        return null;
      }

      const amountMl = volumeToMilliliters(match[2], match[3]);
      if (!Number.isFinite(amountMl)) {
        return null;
      }

      return {
        line,
        rawName: match[1].trim(),
        amountMl,
        originalAmount: match[2],
        originalUnit: normalizeVolumeUnit(match[3]) || match[3],
      };
    })
    .filter(Boolean);

  const totalVolumeMl = volumeEntries.reduce((sum, entry) => sum + entry.amountMl, 0);
  const volumeMap = new Map(volumeEntries.map((entry) => [entry.line, entry]));

  return lines.map((line) => {
    if (isSectionHeaderLine(line)) {
      return null;
    }

    const volumeEntry = volumeMap.get(line);
    if (volumeEntry) {
      const dilution = extractDilutionFactor(volumeEntry.rawName);
      const formulaPercentage = totalVolumeMl > 0
        ? (volumeEntry.amountMl * dilution.factor / totalVolumeMl) * 100
        : null;

      return {
        ingredientName: volumeEntry.rawName,
        lookupName: cleanIngredientName(volumeEntry.rawName),
        formulaPercentage,
        concentrationUnit: "%",
        concentrationVerified: Number.isFinite(formulaPercentage),
        concentrationInput: `${volumeEntry.originalAmount} ${volumeEntry.originalUnit}${dilution.label ? ` @ ${dilution.label}` : ""}`,
        concentrationIssue: Number.isFinite(formulaPercentage)
          ? ""
          : "Total formula volume could not be inferred from the listed batch amounts.",
        sourceLine: line,
        derivedFromVolume: true,
      };
    }

    const trailingMatch = line.match(
      /^(.*?)(?:\s*[:,-]\s*|\s+)(\d*\.?\d+)\s*(%|percent|ppm|mg\s*\/\s*g|mg\s+per\s+g|mg\s*\/\s*ml|mg\s+per\s+ml)?\s*$/i
    );
    const leadingMatch = line.match(
      /^(\d*\.?\d+)\s*(%|percent|ppm|mg\s*\/\s*g|mg\s+per\s+g|mg\s*\/\s*ml|mg\s+per\s+ml)?\s+(.*)$/i
    );

    if (trailingMatch) {
      const concentration = parseConcentration(trailingMatch[2], trailingMatch[3]);
      return {
        ingredientName: trailingMatch[1].trim(),
        lookupName: cleanIngredientName(trailingMatch[1].trim()),
        formulaPercentage: concentration.percentage,
        concentrationUnit: concentration.unit,
        concentrationVerified: concentration.verified,
        concentrationInput: `${trailingMatch[2]}${trailingMatch[3] ? ` ${normalizeUnit(trailingMatch[3])}` : ""}`,
        concentrationIssue: concentration.issue,
        sourceLine: line,
        derivedFromVolume: false,
      };
    }

    if (leadingMatch) {
      const concentration = parseConcentration(leadingMatch[1], leadingMatch[2]);
      return {
        ingredientName: leadingMatch[3].trim(),
        lookupName: cleanIngredientName(leadingMatch[3].trim()),
        formulaPercentage: concentration.percentage,
        concentrationUnit: concentration.unit,
        concentrationVerified: concentration.verified,
        concentrationInput: `${leadingMatch[1]}${leadingMatch[2] ? ` ${normalizeUnit(leadingMatch[2])}` : ""}`,
        concentrationIssue: concentration.issue,
        sourceLine: line,
        derivedFromVolume: false,
      };
    }

    return {
      ingredientName: line,
      lookupName: cleanIngredientName(line),
      formulaPercentage: null,
      concentrationUnit: "",
      concentrationVerified: false,
      concentrationInput: "",
      concentrationIssue: "Concentration value and unit are missing.",
      sourceLine: line,
      derivedFromVolume: false,
    };
  }).filter(Boolean);
};

export const buildParsedResponseFromFormulaText = (inputText, structuredIfraData) => {
  const index = buildIngredientIndex(structuredIfraData);

  return parseFormulaText(inputText).reduce((accumulator, entry) => {
    const lookup = entry.lookupName || entry.ingredientName;
    const normalizedLookup = normalizeText(lookup);
    // Known non-IFRA cosmetic ingredients should be matched explicitly so
    // short names like "Talc" or "Mica" don't fuzzy-match into the fragrance
    // dataset. Bypass IFRA lookup for these.
    const isPassthrough = NON_IFRA_PASSTHROUGH.has(normalizedLookup);
    const match = isPassthrough
      ? null
      : findExactIngredientMatch(index, lookup, "") ||
        findFuzzyIngredientMatch(index, lookup);

    if (match) {
      accumulator[entry.ingredientName] = {
        CAS_NUMBER: match.cas_number,
        common_chemical_name: match.chemical_name,
        concentration_percentage: entry.formulaPercentage,
        concentration_unit: entry.concentrationUnit,
        concentration_verified: entry.concentrationVerified,
        concentration_input: entry.concentrationInput,
        validIngredient: true,
        justification: entry.concentrationIssue,
      };
    } else if (NON_IFRA_PASSTHROUGH.has(normalizeText(entry.lookupName || entry.ingredientName))) {
      // Known cosmetic auxiliary — not in IFRA but valid
      const aliasCas = COMMON_ALIASES[normalizeText(entry.lookupName || entry.ingredientName)] || "N/A";
      accumulator[entry.ingredientName] = {
        CAS_NUMBER: aliasCas,
        common_chemical_name: entry.lookupName || entry.ingredientName,
        concentration_percentage: entry.formulaPercentage,
        concentration_unit: entry.concentrationUnit,
        concentration_verified: entry.concentrationVerified,
        concentration_input: entry.concentrationInput,
        validIngredient: true,
        justification: [
          "Non-IFRA cosmetic ingredient — no fragrance restriction applies.",
          entry.concentrationIssue,
        ]
          .filter(Boolean)
          .join(" "),
      };
    } else {
      accumulator[entry.ingredientName] = {
        CAS_NUMBER: "N/A",
        common_chemical_name: "N/A",
        concentration_percentage: entry.formulaPercentage,
        concentration_unit: entry.concentrationUnit,
        concentration_verified: entry.concentrationVerified,
        concentration_input: entry.concentrationInput,
        validIngredient: false,
        justification: [
          "No IFRA ingredient match found for the provided ingredient name.",
          entry.concentrationIssue,
        ]
          .filter(Boolean)
          .join(" "),
      };
    }

    return accumulator;
  }, {});
};

export const performFuzzySearchAndCheck = (parsedResponse, structuredIfraData) => {
  const index = buildIngredientIndex(structuredIfraData);

  return Object.entries(parsedResponse || {}).map(([ingredientName, ingredient]) => {
    const casNumber = normalizeCas(ingredient?.CAS_NUMBER);
    const formulaPercentage = toStoredPercentage(ingredient?.concentration_percentage);
    const concentrationVerified = ingredient?.concentration_verified === true;
    const concentrationUnit = ingredient?.concentration_unit || "";
    const concentrationIssue =
      ingredient?.concentration_verified === false
        ? ingredient?.justification || "Concentration is unverified."
        : "";

    if (ingredient?.validIngredient === false) {
      return {
        ingredient_name: ingredientName,
        cas_number: casNumber || "N/A",
        formula_percentage: formulaPercentage ?? "N/A",
        category_4_threshold: "N/A",
        above_threshold: false,
        concentration_verified: concentrationVerified,
        concentration_unit: concentrationUnit,
        concentration_issue: concentrationIssue,
        validIngredient: false,
        justification: ingredient?.justification || "Ingredient was marked invalid before IFRA lookup.",
      };
    }

    // Bypass IFRA lookup for known non-IFRA cosmetic ingredients (talc, mica,
    // PFAS polymers, etc.) so their original name and CAS are preserved
    // instead of fuzzy-matching to a similar-letter fragrance ingredient.
    const normalizedName = normalizeText(ingredientName);
    if (NON_IFRA_PASSTHROUGH.has(normalizedName)) {
      return {
        ingredient_name: ingredientName,
        cas_number: casNumber || COMMON_ALIASES[normalizedName] || "N/A",
        formula_percentage: formulaPercentage ?? "N/A",
        category_4_threshold: "N/A",
        above_threshold: false,
        concentration_verified: concentrationVerified,
        concentration_unit: concentrationUnit,
        concentration_issue: concentrationIssue,
        validIngredient: true,
        justification:
          ingredient?.justification ||
          "Non-IFRA cosmetic ingredient — no fragrance restriction applies.",
        match_type: "non-ifra-passthrough",
      };
    }

    const exactMatch = findExactIngredientMatch(index, ingredientName, casNumber);

    // SOTA pattern (Coptis / Formpak / dataEssence): if the ingredient is in
    // CosIng's authoritative inventory, resolve identity from there FIRST
    // and only fuzzy-match against IFRA when that's also a confident hit.
    // This eliminates the silent-rewrite class of bugs where the fuzzy
    // matcher would map "Ethanol" → "Ethylene glycol monomethyl ether and
    // its acetate" because Ethanol has no IFRA Standard of its own.
    const cosingHit = !exactMatch
      ? resolveCosing({ name: ingredientName, cas: casNumber })
      : null;
    const fuzzyMatch = !exactMatch && !cosingHit
      ? findFuzzyIngredientMatch(index, ingredientName)
      : null;
    const fallbackMatch = exactMatch || fuzzyMatch;

    // CosIng-resolved but no IFRA Standard → it's a known cosmetic ingredient,
    // just not one IFRA needs to restrict. Pass it through with the canonical
    // INCI name + CAS instead of silently fuzzy-rewriting it.
    if (!fallbackMatch && cosingHit) {
      return {
        ingredient_name: cosingHit.inciName,
        cas_number: casNumber || cosingHit.casNumbers[0] || "N/A",
        formula_percentage: formulaPercentage ?? "N/A",
        category_4_threshold: "N/A",
        above_threshold: false,
        concentration_verified: concentrationVerified,
        concentration_unit: concentrationUnit,
        concentration_issue: concentrationIssue,
        validIngredient: true,
        justification: `Resolved via CosIng (${cosingHit.matchedBy} match) — no IFRA fragrance restriction applies.`,
        match_type: "cosing-passthrough",
        cosing: cosingHit,
      };
    }

    if (!fallbackMatch) {
      if (ingredient?.validIngredient === true) {
        return {
          ingredient_name: ingredientName,
          cas_number: casNumber || "N/A",
          formula_percentage: formulaPercentage ?? "N/A",
          category_4_threshold: "N/A",
          above_threshold: false,
          concentration_verified: concentrationVerified,
          concentration_unit: concentrationUnit,
          concentration_issue: concentrationIssue,
          validIngredient: true,
          justification:
            ingredient?.justification ||
            "Validated by backend extraction; no local IFRA threshold record was found.",
        };
      }

      return {
        ingredient_name: ingredientName,
        cas_number: casNumber || "N/A",
        formula_percentage: formulaPercentage ?? "N/A",
        category_4_threshold: "N/A",
        above_threshold: false,
        concentration_verified: concentrationVerified,
        concentration_unit: concentrationUnit,
        concentration_issue: concentrationIssue,
        validIngredient: false,
        justification: "No IFRA or CosIng entry matched the provided ingredient.",
      };
    }

    if (
      casNumber &&
      fallbackMatch.cas_numbers?.length &&
      !fallbackMatch.cas_numbers.includes(casNumber)
    ) {
      return {
        ingredient_name: ingredientName,
        cas_number: casNumber,
        formula_percentage: formulaPercentage ?? "N/A",
        category_4_threshold: "N/A",
        above_threshold: false,
        concentration_verified: concentrationVerified,
        concentration_unit: concentrationUnit,
        concentration_issue: concentrationIssue,
        validIngredient: false,
        justification: `Provided CAS number ${casNumber} does not match IFRA record ${fallbackMatch.cas_numbers.join(", ")}.`,
      };
    }

    const category4Threshold =
      fallbackMatch.raw?.usage_restrictions?.categories?.category_4 ?? "N/A";
    const numericThreshold = Number(category4Threshold);
    // Threshold of 0 means unrestricted in IFRA (no limit set), not "prohibited at any concentration"
    const hasRealThreshold = Number.isFinite(numericThreshold) && numericThreshold > 0;
    const aboveThreshold =
      Number.isFinite(formulaPercentage) &&
      concentrationVerified &&
      hasRealThreshold &&
      formulaPercentage > numericThreshold;

    return {
      document_name: fallbackMatch.docName,
      ingredient_name: fallbackMatch.raw?.ingredient?.name || ingredientName,
      cas_number: casNumber || fallbackMatch.cas_number || "N/A",
      formula_percentage: formulaPercentage ?? "N/A",
      category_4_threshold: hasRealThreshold ? numericThreshold : "N/A",
      above_threshold: aboveThreshold,
      concentration_verified: concentrationVerified,
      concentration_unit: concentrationUnit,
      concentration_issue: concentrationIssue,
      validIngredient: true,
      justification: ingredient?.justification || "",
      match_type: fallbackMatch.matchType,
    };
  });
};
